import {
  addToMultiMarket,
  checkIncludeMultiMarket,
  EventDTO,
  formatTime,
  getCashoutBet,
  getMarketStatus,
  getOutcomeId,
  langData,
  MatchOddsDTO,
  MatchOddsRunnerDTO,
  removeToMultiMarket,
  setLoading,
  showToast,
  useAppDispatch,
  useAppSelector,
  UserBet,
  addExchangeBet,
  PlaceBetRequest,
  useMatchOdds,
  CURRENCY_TYPE_FACTOR,
  getCurrencyTypeFromToken,
} from "@hypexone/uicore";
import React from "react";
import { useMarketLocalState } from "../../../../utils/hooks/storage.hooks";
import MarketsTitle from "../market-table/MarketsTitle";
import MarketTableHeading from "../market-table/MarketTableHeading";
import MatchOddsRow from "./MatchOddsRow";
import Button from "../../shared/Button/Button";
import { BRAND_DOMAIN } from "../../../../constants/Branding";
import Modal from "../../shared/ModalComponent";
import { API_CLIENT } from "../../../../App.utils";
import { colors } from "../../../../styles/colors";
import PinIconGray from "../../../../assets/icons/events-icons/pin-icon-gray.svg";
import PinIconGreen from "../../../../assets/icons/events-icons/pin-icon-green.svg";
import InfoIconRoundedWhite from "../../../../assets/icons/info-icon-rounded-white.svg";
import { isMobile } from "react-device-detect";
import Popup from "../../shared/ModalComponent";
import OneClickBettingCountdown from "../../one-click-betting/OneClickBettingCountdown";
import { BsFillPinFill } from "react-icons/bs";
import MarqueeText from "../../MarqueeText/MarqueeText";

export type CashoutInfo = {
  selectionId: string;
  betType: any;
  oddValue: number;
  stakeAmount: number;
};

interface MatchOddsProps {
  eventData: EventDTO;
  secondaryMatchOdds: MatchOddsDTO[];
  loggedIn?: boolean;
  exposureMap: any;
  setBetStartTime?: Function;
  setAddNewBet?: Function;
  isMultiMarket?: boolean;
  getFormattedMinLimit: (num: number) => string;
  getFormattedMaxLimit: (num: number) => string;
  showMatchOdds: boolean;
  showSecondaryMatchOdds: boolean;
}

const MatchOdds: React.FC<MatchOddsProps> = ({
  eventData,
  secondaryMatchOdds,
  isMultiMarket,
  exposureMap,
  setAddNewBet,
  setBetStartTime,
  showMatchOdds,
}) => {
  const dispatch = useAppDispatch();
  const { selectedEventType } = useAppSelector((state) => state.catalog);
  const {
    bets,
    openBets,
    isOneClickBettingLoading,
    bettingInprogress,
    cashoutInProgress,
    betStatusResponse,
  } = useAppSelector((state) => state.betSlip);
  const { loading, loggedIn } = useAppSelector((state) => state.auth);
  const cashoutEnabled = useAppSelector((state) => state.common.domainConfig?.cashoutEnabled === true);
  const [multiMarketData, setMultiMarketData] = useMarketLocalState();
  const cFactor = CURRENCY_TYPE_FACTOR[getCurrencyTypeFromToken()];
  // Helper function to check if market is suspended (considering event-level suspend)
  const isMarketSuspended = (marketSuspend: boolean) => {
    return eventData?.eventSuspended === true ? true : marketSuspend;
  };

  // Initialize useMatchOdds hook
  const hookResult = useMatchOdds({
    eventData,
    secondaryMatchOdds,
    exposureMap,
    bets,
    openBets,
    cashoutInProgress,
    betStatusResponse,
    loggedIn: loggedIn || false,
    isMultiMarket,
    API: API_CLIENT,
    setBetStartTime: setBetStartTime as ((date: Date) => void) | undefined,
    setAddNewBet: setAddNewBet as ((val: boolean) => void) | undefined,
    multiMarketData,
    setMultiMarketData,
    bettingInprogress,
    isOneClickBettingLoading,
  });

  // Destructure hook values
  const {
    matchOddsData,
    confirmCashout,
    setConfirmCashout,
    coMarket,
    setCoMarket,
    selectedRow,
    setSelectedRow,
    isPopupVisible,
    openPopup,
    closePopup,
    disabledStatus,
    calCashout,
    getCashoutProfit: hookGetCashoutProfit,
    isHaveCashOut,
    autoCashout,
    getOpenBetsPLInArray,
    getTotalPL: hookGetTotalPL,
    handleMultiMarket,
    getMarketAlerts,
    getCashoutDisabled,
    // getTurboInfo,
    getCashoutState,
    // setConfirmTurboCashout,
    // confirmTurboCashout,
    // turboCoMarket,
    // setTurboCoMarket,
    // turboCashoutFunction,
    // getTurboCashoutData,
    // turboCashoutLoading,
    // isLossCutAvailable,
    // getLossCutProfit,
    // getLossCutDisabled,
    // autoLossCut,
  } = hookResult;

  // Wrapper function for getCashoutProfit to match Laser247's expected format
  // Hook's getCashoutProfit returns riskAfter value, but Laser247 expects profit
  // Calculate profit from cashout response similar to old Laser247 logic

  const getCashoutProfit = (market: MatchOddsDTO) => {
    if (market?.runners?.length !== 2) return 0;

    const key = `${market.marketId}:${market.marketName}`;
    const riskRows = exposureMap[key];
    if (!riskRows || riskRows.length < 2) return 0;

    const response = calCashout(market);
    if (!response || !response.stake || response.stake === 0) return Infinity;

    // Calculate profit from current risk and cashout stake
    // Find the runner that is NOT the cashout runner (the one we're hedging against)
    const otherRunner = riskRows.find(
      (r: any) => r.runnerId !== response.runnerId,
    );
    if (!otherRunner) return Infinity;

    const currentRisk = Math.abs(Number(otherRunner.userRisk) || 0);
    const stake = Number(response.stake || 0);

    // Profit = (stake - currentRisk) / cFactor
    // If BACK bet, negate it
    let profit = (stake - currentRisk) / cFactor;
    if (response.betType === "BACK") {
      profit = -1 * profit;
    }

    // Return Infinity if calculation results in invalid value
    if (!isFinite(profit)) return Infinity;

    return +profit.toFixed(2);
  };

  // Wrapper for getTotalPL to match Laser247 MatchOddsRow expected signature (5 params)
  const getTotalPL = (
    marketId: string,
    marketName: string,
    runner: MatchOddsRunnerDTO,
    bets: PlaceBetRequest[],
    exposure: any,
  ) => {
    // Hook's getTotalPL uses bets from hook props, so we can ignore the bets param
    // But we need to ensure it matches the expected signature
    return hookGetTotalPL(marketId, marketName, runner);
  };

  // Wrapper for getOpenBetsPLInArray to match Laser247 MatchOddsRow expected signature (4 params)
  const getOpenBetsPLInArrayWrapper = (
    marketId: string,
    marketName: string,
    runner: MatchOddsRunnerDTO,
    type: string,
  ) => {
    // Hook's getOpenBetsPLInArray doesn't need the type param, so we ignore it
    return getOpenBetsPLInArray(marketId, marketName, runner);
  };

  const rules = [
    "cricket_match_odds",
    "tennis_match_odds",
    "football_match_odds",
    "football_under_over_goals",
  ];
  const highLightedIndices = [];

  // Render market function to eliminate code duplication
  const renderMarket = (
    marketData: MatchOddsDTO,
    isPrimary: boolean,
    idx?: number,
  ) => {
    if (!marketData) return null;

    // const {
    //   isAvailable: isTurboAvailable,
    //   label: turboLabel,
    //   countdown: countdownVal,
    // } = getTurboInfo(marketData);
    const {
      isProfitable,
      label: cashoutLabel,
      profit,
    } = getCashoutState(marketData);

    const cashoutDisabled = getCashoutDisabled
      ? getCashoutDisabled(marketData)
      : bettingInprogress ||
        !isHaveCashOut(marketData) ||
        disabledStatus.includes(marketData?.status?.toLowerCase()) ||
        isMarketSuspended(marketData.suspend) ||
        !(`${marketData?.marketId}:${marketData?.marketName}` in exposureMap) ||
        profit === Infinity ||
        profit === 0;

    const isMultiMarketIncluded = checkIncludeMultiMarket(
      multiMarketData,
      marketData?.marketId,
    );

    if (isMultiMarket && !isMultiMarketIncluded) return null;

    // Use hook helpers
    const { showCountdown } = getMarketAlerts(marketData);

    const minStakeByCurrency = marketData?.marketLimits?.minStake
      ? marketData?.marketLimits?.minStake / cFactor
      : 0;

    const maxStakeByCurrency = marketData?.marketLimits?.maxStake
      ? marketData?.marketLimits?.maxStake / cFactor
      : 0;

    return (
      <div className="relative flex flex-col w-full">
        {showCountdown && (
          <OneClickBettingCountdown
            delay={marketData?.marketLimits?.delay || 0}
          />
        )}

        <div className="flex items-center justify-between w-full bg-blue4 px-2 py-1.5 text-xs text-white font-bold">
          <div className="flex items-center w-full">
            {!checkIncludeMultiMarket(multiMarketData, marketData.marketId) ? (
              <span title="Add To Multi Markets">
                <BsFillPinFill
                  className="multi-add-icon cursor-pointer h-[100%] text-white hidden md:block"
                  onClick={(e) => {
                    e.stopPropagation();
                    multiMarketData?.length < 10 &&
                      handleMultiMarket(
                        eventData?.competitionId,
                        eventData?.eventId,
                        marketData.marketId,
                        eventData?.providerName,
                        eventData?.sportId,
                        true,
                      );
                  }}
                />
                <img
                  src={PinIconGray}
                  alt="multipin"
                  className="w-5 h-5 md:hidden cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    multiMarketData?.length < 10 &&
                      handleMultiMarket(
                        eventData?.competitionId,
                        eventData?.eventId,
                        marketData.marketId,
                        eventData?.providerName,
                        eventData?.sportId,
                        true,
                      );
                  }}
                />
              </span>
            ) : (
              <span title="Remove From Multi Markets">
                <BsFillPinFill
                  className="multi-remove-icon cursor-pointer h-[100%] text-green-500 hidden md:block"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMultiMarket(
                      eventData?.competitionId,
                      eventData?.eventId,
                      marketData.marketId,
                      eventData?.providerName,
                      eventData?.sportId,
                      false,
                    );
                  }}
                />
                <img
                  src={PinIconGreen}
                  alt="multipin"
                  className="w-5 h-5 md:hidden cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMultiMarket(
                      eventData?.competitionId,
                      eventData?.eventId,
                      marketData.marketId,
                      eventData?.providerName,
                      eventData?.sportId,
                      false,
                    );
                  }}
                />
              </span>
            )}

            <div className="ml-2 flex items-center gap-2">
              <div className="capitalize">
                {marketData?.marketName?.toLowerCase()}
              </div>
              <div className="cursor-pointer">
                <img
                  onClick={openPopup}
                  src={InfoIconRoundedWhite}
                  alt="info-icon"
                  className="h-[13px] w-[13px] min-w-[13px]"
                />
              </div>
              {secondaryMatchOdds &&
                ["7", "4339"].includes(selectedEventType?.id) && (
                  <span className="market-start-time">
                    {formatTime(marketData?.marketTime || new Date())}
                  </span>
                )}
              {["7", "4339"].includes(selectedEventType?.id) && (
                <span
                  className={
                    getMarketStatus(marketData?.marketTime || new Date()) ===
                    "OPEN"
                      ? " bg-[green] web-view"
                      : " bg-[red] web-view"
                  }
                >
                  {getMarketStatus(marketData?.marketTime || new Date())}
                </span>
              )}
              {isMultiMarket && !isMobile && (
                <span className="pl-2">{eventData?.eventName}</span>
              )}
            </div>
          </div>
          {/* Right: Cashout / Turbo and Loss Cut */}
          <div className="flex items-center gap-1">
            {cashoutEnabled &&
              marketData?.runners?.length === 2 &&
              !marketData?.marketName?.toUpperCase()?.includes("TOSS") && (
                <>
                  <Button
                    style={{ background: "#701920" }}
                    className={`px-2 tracking-wide text-white font-bold leading-none relative overflow-hidden text-[11px] transition-all duration-150 ease-in-out rounded-md text-center flex items-center justify-center flex-row h-[26px] cursor-pointer shadow-[0_2px_6px_rgba(0,0,0,0.35)] border border-white/20 ${
                      isProfitable ? "profit" : "loss"
                    }`}
                    disabled={cashoutDisabled}
                    loading={
                      loading ||
                      (cashoutInProgress?.loading &&
                        cashoutInProgress?.marketId === marketData.marketId)
                    }
                    onClick={() => autoCashout(marketData)}
                  >
                    <span className="text-white whitespace-nowrap">
                      {(langData?.["cashout"] ?? "Cashout").toUpperCase()}
                      {cashoutLabel != null && cashoutLabel !== ""
                        ? ` ${cashoutLabel}`
                        : ""}
                    </span>
                  </Button>
                  {/* Speed Cash / Loss Cut - disabled
                    {!isTurboAvailable ? null : (
                      <Button
                        style={{ background: "#5bb450" }}
                        className="px-2 tracking-wide text-white font-bold leading-none relative overflow-hidden text-[11px] transition-all duration-150 ease-in-out rounded-md text-center flex items-center justify-center flex-row h-[26px] cursor-pointer shadow-[0_2px_6px_rgba(0,0,0,0.35)] border border-white/20"
                        disabled={
                          !isTurboAvailable ||
                          disabledStatus.includes(marketData?.status?.toLowerCase()) ||
                          isMarketSuspended(marketData.suspend) ||
                          !(
                            `${marketData?.marketId}:${marketData?.marketName}` in
                            exposureMap
                          ) ||
                          turboCashoutLoading
                        }
                        loading={loading}
                        onClick={() => {
                          if (countdownVal > 0) return;
                          setTurboCoMarket(marketData);
                          setConfirmTurboCashout(true);
                        }}
                      >
                        <span className="text-white whitespace-nowrap">
                          {turboLabel || "SPEED CASH"}
                        </span>
                      </Button>
                    )}
                    <Button
                      className="loss-cut-btn px-2 tracking-wide text-white font-bold leading-none relative overflow-hidden text-[11px] transition-all duration-150 ease-in-out rounded-md text-center flex items-center justify-center flex-row h-[26px] cursor-pointer shadow-[0_2px_6px_rgba(0,0,0,0.35)] border border-white/20"
                      style={{ background: "#5bb450" }}
                      disabled={
                        bettingInprogress ||
                        (getLossCutDisabled && getLossCutDisabled(marketData)) ||
                        (cashoutInProgress?.loading &&
                          cashoutInProgress?.marketId === marketData.marketId)
                      }
                      loading={
                        loading ||
                        (cashoutInProgress?.loading &&
                          cashoutInProgress?.marketId === marketData.marketId)
                      }
                      onClick={() => autoLossCut && autoLossCut(marketData)}
                    >
                      <span className="text-white whitespace-nowrap">
                        {(langData?.["loss_cut"] || "Loss Cut").toUpperCase()}
                        {typeof getLossCutProfit === "function" &&
                        getLossCutProfit(marketData) != null
                          ? ` : ₹${Number(getLossCutProfit(marketData)).toFixed(2)}`
                          : ""}
                      </span>
                    </Button>
                  */}
                </>
              )}
          </div>

          <Modal
            isOpen={isPopupVisible}
            onClose={closePopup}
            content={<div>Popup Content</div>}
            title="Rules"
          >
            {rules.map((point, index) => (
              <div
                key={index}
                className={`p-[8px] border-b font-sans font-[450] border-gray-300 flex items-center text-[12px]  ${
                  highLightedIndices.includes(index)
                    ? "text-[#F1556C]"
                    : " text-[#000]"
                }`}
              >
                {langData?.[point] || point}
              </div>
            ))}
          </Modal>
        </div>

        <div className="flex flex-col bg-white border border-solid border-neutral-200 max-md:max-w-full">
          <table className="w-full text-[14px] font-medium [&_td]:p-[0.2px] [&_th]:p-0">
            <MarketTableHeading
              cashOutButton={
                cashoutEnabled &&
                marketData?.runners?.length === 2 &&
                !marketData?.marketName?.toUpperCase()?.includes("TOSS") &&
                !isPrimary &&
                getCashoutState(marketData).hasCashout ? (
                  <div className="h-[28px]">
                    <Button
                      color="primary"
                      className="h-[28px] w--auto bg-[#b98eb1] text-[#fff] cursor-pointer px-2 flex items-center"
                      disabled={
                        disabledStatus.includes(
                          marketData?.status?.toLowerCase(),
                        ) ||
                        isMarketSuspended(marketData.suspend) ||
                        !(
                          `${marketData?.marketId}:${marketData?.marketName}` in
                          exposureMap
                        )
                      }
                      onClick={() => autoCashout(marketData)}
                    >
                      Cashout
                    </Button>
                  </div>
                ) : null
              }
              title1={isPrimary ? "back" : "Back"}
              title2={isPrimary ? "lay" : "Lay"}
              minStake={minStakeByCurrency}
              maxStake={maxStakeByCurrency}
            />
            <tbody>
              {!isPrimary &&
              idx === 0 &&
              ["7", "4339"].includes(selectedEventType?.id) ? (
                <label
                  className={
                    getMarketStatus(marketData?.marketTime || new Date()) ===
                    "OPEN"
                      ? "profit mob-view"
                      : "loss mob-view"
                  }
                >
                  {getMarketStatus(marketData?.marketTime || new Date())}
                </label>
              ) : null}
              {marketData?.runners?.length > 0 &&
                marketData?.runners
                  .filter((runner) => runner?.status?.toLowerCase() !== "loser")
                  .map((runner, runnerIdx) => (
                    <MatchOddsRow
                      key={runnerIdx}
                      minStake={minStakeByCurrency}
                      maxStake={maxStakeByCurrency}
                      eventData={eventData}
                      marketName={marketData.marketName || "Match Odds"}
                      matchOddsData={marketData}
                      runner={runner}
                      getOpenBetsPL={getOpenBetsPLInArrayWrapper}
                      getTotalPL={getTotalPL}
                      disabledStatus={disabledStatus}
                      bets={bets}
                      selectedRow={selectedRow}
                      setSelectedRow={setSelectedRow}
                      setAddNewBet={setAddNewBet}
                      setBetStartTime={setBetStartTime}
                      exposure={exposureMap}
                    />
                  ))}

              {marketData?.notification && (
                <tr>
                  <td colSpan={12} className="overflow-hidden">
                    <MarqueeText text={marketData.notification ?? ""} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      {matchOddsData?.runners?.length > 0 &&
      matchOddsData?.status?.toLowerCase() !== "closed" &&
      showMatchOdds
        ? renderMarket(matchOddsData, true)
        : null}

      {/* Secondary Match Odds */}
      {(secondaryMatchOdds ?? []).map((moData, idx) => (
        <React.Fragment key={idx}>
          {moData?.status?.toLowerCase() !== "closed" &&
          !moData?.marketName?.toLowerCase().includes("who will win the match")
            ? renderMarket(moData, false, idx)
            : null}
        </React.Fragment>
      ))}

      <Modal
        title="Do you wish to perform cash out?"
        isOpen={confirmCashout}
        onClose={() => setConfirmCashout(false)}
      >
        <div className="grid grid-cols-2 w-full gap-x-2">
          {/* Cancel Button */}
          <Button
            type="button"
            onClick={() => setConfirmCashout(false)}
            className="leading-normal relative overflow-hidden transition duration-150 ease-in-out bg-transparent w-full px-2.5 py-1 border-[1px] border-primary rounded-[4px] flex items-center justify-center cursor-pointer"
          >
            <span className="text-text_Primary text-[16px] font-[700] font-lato">
              cancel
            </span>
          </Button>

          {/* Cashout Button */}
          <div className="w-full">
            <Button
              type="button"
              disabled={loading}
              onClick={() => {
                setLoading(true);
                autoCashout(coMarket);
                setConfirmCashout(false);
              }}
              className="leading-normal relative overflow-hidden transition duration-150 ease-in-out bg-[#29ab2b] w-full h-[36px] min-h-[36px] px-2.5 py-1 border-[1px] border-success rounded-[4px] flex items-center justify-center cursor-pointer"
            >
              <span className="text-white text-[16px] font-[700] font-lato">
                cashout
              </span>
            </Button>
          </div>
        </div>
      </Modal>

      {/* Turbo Cashout (Speed Cash) Modal - disabled
      <Modal
        title="Speed Cash"
        isOpen={!!confirmTurboCashout}
        onClose={() => {
          setConfirmTurboCashout?.(false);
          setTurboCoMarket?.(null);
        }}
      >
        {turboCoMarket && (
          <>
            <div className="grid grid-cols-2 mb-2 border border-dashed border-black">
              {turboCoMarket.runners?.map((runner) => {
                const key = `${turboCoMarket.marketId}:${turboCoMarket.marketName}`;
                const riskRows = exposureMap[key] as any[];
                const riskMap = new Map(
                  riskRows?.map((r: any) => [r.runnerId, Number(r.userRisk) || 0]) ?? [],
                );
                const risk = Number(riskMap.get(runner.runnerId) ?? 0);
                return (
                  <div
                    key={runner.runnerId}
                    className="text-center py-1 border-r border-dashed border-black last:border-r-0"
                  >
                    <div className="text-text_Primary text-[16px] font-[700] font-lato mb-1">
                      {runner.runnerName}
                    </div>
                    <div className="text-text_Primary text-[14px] font-[500] font-lato">
                      ₹{risk.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center mb-2">
              <span className="text-text_Primary text-[14px] font-[500] font-lato">
                We are deducting 3% fee on speed cashout.
              </span>
            </div>
            <div className="grid grid-cols-1 w-full gap-2">
              <Button
                type="button"
                disabled={loading}
                onClick={async () => {
                  try {
                    await turboCashoutFunction?.(turboCoMarket);
                    setConfirmTurboCashout?.(false);
                    setTurboCoMarket?.(null);
                  } catch (error: any) {
                    dispatch(
                      showToast({
                        type: "error",
                        message:
                          error?.response?.data?.message || "Speed cash failed",
                      }),
                    );
                  } finally {
                    setConfirmTurboCashout?.(false);
                    setTurboCoMarket?.(null);
                  }
                }}
                className="leading-normal relative overflow-hidden transition duration-150 ease-in-out bg-[#ff6b35] w-full min-h-[36px] px-2.5 py-3 border-[1px] border-orange-500 rounded-[4px] flex items-center justify-center cursor-pointer"
              >
                <span className="text-white text-[16px] font-[700] font-lato">
                  {loading
                    ? "Processing..."
                    : `SPEED CASH - ₹${
                        getTurboCashoutData?.(turboCoMarket)?.turboCashoutAmount ?? "0.00"
                      }`}
                </span>
              </Button>
            </div>
          </>
        )}
      </Modal>
      */}
    </>
  );
};

export default MatchOdds;
